import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../ui/Button/Button';
import Icon from '../Icon/Icon';
import { IconKeys } from '../Icon/IconKeys';
import {
  buildListNavigationEntries,
  getListNavigationContext,
  getListNavigationReturnPath,
  ListNavigationEntity,
  ListNavigationEntry,
  ListNavigationSourceItem,
  requestListItemFocus,
} from '../../../utils/listNavigation';
import { shouldIgnoreArrowNavigation } from './arrowKeyNavigation';
import { withScrollRestoreState } from '../../../utils/scrollRestoration';
import { getAlertsWithInvalid } from '../../../API/services/AlertsService';
import { getTrips } from '../../../API/services/TripsService';
import { getDrivers } from '../../../API/services/DriversService';
import { getDevices } from '../../../API/services/DeviceService';
import S from './DetailsListNavigationStyles';

type ListNavigationFetcher = (
  fleetId: string,
  query: string,
) => Promise<{ results?: ListNavigationSourceItem[] }>;

const listFetchers: Record<ListNavigationEntity, ListNavigationFetcher> = {
  alerts: getAlertsWithInvalid as unknown as ListNavigationFetcher,
  trips: getTrips as unknown as ListNavigationFetcher,
  drivers: getDrivers as unknown as ListNavigationFetcher,
  devices: getDevices as unknown as ListNavigationFetcher,
};

type Props = {
  entity: ListNavigationEntity;
  currentId?: string;
};

export type DetailsListNavigationResult = {
  controls: JSX.Element | null;
  backToList: (() => void) | null;
};

function useDetailsListNavigation({
  entity,
  currentId,
}: Props): DetailsListNavigationResult {
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = useMemo(
    () => getListNavigationContext(location.state, entity),
    [entity, location.state],
  );
  const context = navigation?.context;
  const needsFullList = !!context
    && context.isComplete !== true
    && !!context.fleetId
    && !!context.query;
  const { data: fullListData } = useQuery(
    ['list-navigation', entity, context?.fleetId ?? '', context?.query ?? ''],
    async () => listFetchers[entity](context?.fleetId ?? '', context?.query ?? ''),
    {
      enabled: needsFullList,
      retry: 1,
      retryDelay: 3000,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
  const entries = useMemo(() => {
    if (!context) return [];
    if (needsFullList && fullListData?.results?.length) {
      return buildListNavigationEntries(fullListData.results, entity, context.pageSize);
    }
    return context.entries;
  }, [context, entity, fullListData, needsFullList]);
  const currentIndex = navigation
    ? entries.findIndex(
      (entry) => entry.itemId === navigation.state.itemId && entry.routeId === currentId,
    )
    : -1;
  const currentEntry = currentIndex >= 0 ? entries[currentIndex] : undefined;
  const previousEntry = currentIndex > 0 ? entries[currentIndex - 1] : undefined;
  const nextEntry = currentIndex >= 0 ? entries[currentIndex + 1] : undefined;

  const navigateToEntry = useCallback((entry?: ListNavigationEntry): void => {
    if (!entry || !navigation) return;
    navigate(`/${entity}/${entry.routeId}${location.search}`, {
      replace: true,
      state: {
        ...(location.state && typeof location.state === 'object' ? location.state : {}),
        ...(entry.routeState || {}),
        listNavigation: {
          contextKey: navigation.state.contextKey,
          itemId: entry.itemId,
        },
      },
    });
  }, [entity, location.search, location.state, navigate, navigation]);

  useEffect(() => {
    if (!previousEntry && !nextEntry) return undefined;

    const handleArrowKey = (event: KeyboardEvent): void => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (shouldIgnoreArrowNavigation(event)) return;

      const targetEntry = event.key === 'ArrowLeft' ? previousEntry : nextEntry;
      if (!targetEntry) return;

      event.preventDefault();
      navigateToEntry(targetEntry);
    };

    document.addEventListener('keydown', handleArrowKey);

    return () => document.removeEventListener('keydown', handleArrowKey);
  }, [navigateToEntry, nextEntry, previousEntry]);

  if (!navigation || !currentEntry) {
    return { controls: null, backToList: null };
  }

  const returnPath = getListNavigationReturnPath(navigation.context, currentEntry);
  const backToList = (): void => {
    requestListItemFocus(returnPath, currentEntry.itemId);
    const originPage = Number(new URLSearchParams(
      navigation.context.returnPath.split('?')[1] || '',
    ).get('page') || 1);
    if (currentEntry.page === originPage && window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }
    navigate(returnPath, {
      replace: true,
      state: withScrollRestoreState(navigation.context.returnState),
    });
  };

  const controls = (
    <S.Wrapper className="list-navigation-controls">
      <Button
        height="44px"
        padding="10px 6px"
        title="Previous item"
        aria-label="Previous item"
        disabled={!previousEntry}
        onClick={() => {
          if (previousEntry) navigateToEntry(previousEntry);
        }}
      >
        <Icon iconKey={IconKeys.ROUNDED_ARROW_LEFT} />
      </Button>
      <Button
        height="44px"
        padding="10px 6px"
        title="Next item"
        aria-label="Next item"
        disabled={!nextEntry}
        onClick={() => {
          if (nextEntry) navigateToEntry(nextEntry);
        }}
      >
        <Icon iconKey={IconKeys.ROUNDED_ARROW_RIGHT} />
      </Button>
    </S.Wrapper>
  );

  return { controls, backToList };
}

export default useDetailsListNavigation;
